import React, { useState, useEffect } from "react";
import { Sun, Moon, ChevronUp, ChevronDown } from 'lucide-react';
import Navbar from "../components/Navbar";
import apiClient from '../utils/apiClient';
import { getImageUrl } from '../utils/apiConfig';
import Footer from "../components/Footer";
import Select from "react-select";
import { useIsMobile } from '../hooks/useIsMobile';
import QuotaCard from '../components/QuotaCard';
import QuotasHero from '../components/QuotasHero';

const QuotasPage = () => {
  const currentYear = new Date().getFullYear().toString();
  const [quotas, setQuotas] = useState([]);
  const [filteredQuotas, setFilteredQuotas] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "average_score",
    direction: "desc",
  });
  const [latestUpdateTime, setLatestUpdateTime] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(currentYear);
  const [availableYears, setAvailableYears] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const isMobile = useIsMobile();

  // Initialize dark mode from localStorage/system preference
  useEffect(() => {
    const stored = localStorage.getItem('theme');
    if (stored) {
      setIsDarkMode(stored === 'dark');
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setIsDarkMode(true);
    }
  }, []);

  // Sync dark mode to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // Get player image URL
  const getPlayerImageUrl = (player) => {
    if (player?.image_path) {
      return getImageUrl(player.image_path);
    }
    return '/default-avatar.png';
  };

  // Fetch available years from the backend
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await apiClient.get('/quotas/years');
        const years = response.data.years.map((year) => ({
          value: year.toString(),
          label: year.toString(),
        }));
        setAvailableYears([{ value: "All Time", label: "All Time" }, ...years]);
      } catch (error) {
        // Silently handle years fetch error
      }
    };

    fetchYears();
  }, []);

  // Fetch quotas based on the selected season
  useEffect(() => {
    const fetchQuotas = async () => {
      try {
        const startDate =
          selectedSeason === "All Time" ? null : `${selectedSeason}-01-01`;
        const endDate =
          selectedSeason === "All Time" ? null : `${selectedSeason}-12-31`;

        const response = await apiClient.get('/quotas', {
          params: { startDate, endDate },
        }
        );

        const processedQuotas = response.data.quotas
          .map((player) => ({
            id: player.player_id,
            name: player.player_name || "N/A",
            image_path: player.image_path,
            current_quota: player.current_quota || 0,
            lowest_score: player.lowest_score || 0,
            best_score: player.best_score || 0,
            average_score: parseFloat(player.average_score) || 0,
            date: player.date ? new Date(player.date) : new Date(),
            events_played: player.events_played || 0,
            quota_change: player.quota_change != null ? parseInt(player.quota_change) : null,
            times_beat_quota: player.times_beat_quota != null ? parseInt(player.times_beat_quota) : 0,
            beat_quota_pct: player.beat_quota_pct != null ? parseFloat(player.beat_quota_pct) : 0,
          }))
          .filter((player) => {
            // Only filter if events_played data exists
            return player.events_played > 0 ? player.events_played >= 5 : true;
          });

        setQuotas(processedQuotas);
        setFilteredQuotas(processedQuotas);
        setLatestUpdateTime(response.data.latest_update);
      } catch (error) {
        // Silently handle quotas fetch error
      }
    };

    fetchQuotas();
  }, [selectedSeason]);

  const handleSeasonChange = (selectedOption) => {
    setSelectedSeason(selectedOption.value);
    const filteredData =
      selectedOption.value === "All Time"
        ? quotas
        : quotas.filter(
          (quota) =>
            quota.date >= new Date(`${selectedOption.value}-01-01`) &&
            quota.date <= new Date(`${selectedOption.value}-12-31`)
        );
    setFilteredQuotas(filteredData);
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "desc"
          ? "asc"
          : "desc",
    }));
  };

  const sortedQuotas = [...filteredQuotas].sort((a, b) => {
    const { key, direction } = sortConfig;
    const aValue = a[key] ?? 0;
    const bValue = b[key] ?? 0;

    if (typeof aValue === "number" && typeof bValue === "number") {
      return direction === "asc" ? aValue - bValue : bValue - aValue;
    }

    if (aValue < bValue) return direction === "asc" ? -1 : 1;
    if (aValue > bValue) return direction === "asc" ? 1 : -1;
    return 0;
  });

  // Dynamic labels based on selected season
  const isAllTime = selectedSeason === "All Time";
  const scoreLabel = isAllTime ? "All Time" : `${selectedSeason}`;
  const averageLabel = isAllTime ? "All Time" : `${selectedSeason}`;

  // Custom styles for select dropdown with dark mode support
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "38px",
      fontSize: "14px",
      backgroundColor: isDarkMode ? "#1e3a5f" : "white",
      borderColor: isDarkMode ? "#475569" : "#d1d5db",
      borderRadius: "0.75rem",
      color: isDarkMode ? "#e5e7eb" : "black",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? "#e5e7eb" : "black",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "4px",
      color: isDarkMode ? "#9ca3af" : "#6b7280",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "4px 8px",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? "#1e3a5f" : "white",
      border: isDarkMode ? "1px solid #475569" : "1px solid #d1d5db",
      borderRadius: "0.75rem",
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: "14px",
      backgroundColor: state.isSelected
        ? (isDarkMode ? "#2563eb" : "#cbd5e1")
        : (isDarkMode ? "#1e3a5f" : "white"),
      color: isDarkMode ? "#e5e7eb" : "black",
      "&:hover": {
        backgroundColor: isDarkMode ? "#374151" : "#e2e8f0",
      },
    }),
  };

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === "asc"
      ? <ChevronUp className="w-4 h-4 inline ml-1" />
      : <ChevronDown className="w-4 h-4 inline ml-1" />;
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-navy-950 flex flex-col transition-colors duration-300">
      <Navbar />

      <main className="flex-grow">
        {/* Hero Header Section */}
        <div className="bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-navy-800">
          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-6">
                <div>
                  <h1 className="text-3xl md:text-4xl font-display font-bold text-forest-900 dark:text-white tracking-tight">
                    Scores Overview
                  </h1>
                  <p className="text-gray-600 dark:text-navy-400 mt-1">
                    Stableford Performance Stats
                  </p>
                </div>

                {/* Season Selector */}
                <div className="hidden sm:block">
                  <Select
                    id="season-selector"
                    options={availableYears}
                    value={availableYears.find((option) => option.value === selectedSeason)}
                    onChange={handleSeasonChange}
                    className="w-32"
                    styles={customStyles}
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Updated Time */}
                {latestUpdateTime && (
                  <div className="hidden md:flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-forest-500 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-forest-500"></span>
                    </span>
                    Updated {new Date(latestUpdateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(latestUpdateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </div>
                )}

                {/* Dark Mode Toggle */}
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="
                    flex items-center gap-2 px-4 py-2 rounded-full
                    bg-gray-100 dark:bg-navy-800
                    text-gray-700 dark:text-gray-200
                    hover:bg-gray-200 dark:hover:bg-navy-700
                    transition-colors duration-200
                    border border-gray-200 dark:border-navy-700
                  "
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? (
                    <>
                      <Sun className="w-5 h-5 text-gold-400" />
                      <span className="text-sm font-medium hidden sm:inline">Light</span>
                    </>
                  ) : (
                    <>
                      <Moon className="w-5 h-5 text-navy-600" />
                      <span className="text-sm font-medium hidden sm:inline">Dark</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Mobile Season Selector */}
            <div className="sm:hidden mb-4">
              <Select
                id="season-selector-mobile"
                options={availableYears}
                value={availableYears.find((option) => option.value === selectedSeason)}
                onChange={handleSeasonChange}
                className="w-full"
                styles={customStyles}
              />
            </div>

            {/* Hero Section with Highlights */}
            <QuotasHero
              players={sortedQuotas}
              selectedSeason={selectedSeason}
              getPlayerImageUrl={getPlayerImageUrl}
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Info Row */}
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              * Only players with a minimum of 5 events played are shown.
            </div>
            <div className="text-sm text-gray-500 dark:text-navy-400 tabular-nums">
              {sortedQuotas.length} player{sortedQuotas.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Table/Cards */}
          {isMobile ? (
            <div className="space-y-4">
              {sortedQuotas.map((player, index) => (
                <QuotaCard
                  key={index}
                  player={player}
                  scoreLabel={scoreLabel}
                  averageLabel={averageLabel}
                />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl shadow-lg bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800">
              <table className="w-full">
                <thead>
                  <tr className="bg-forest-900 dark:bg-navy-800 text-white">
                    <th
                      className="p-4 text-left font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => handleSort("name")}
                    >
                      Name<SortIndicator columnKey="name" />
                    </th>
                    <th
                      className="p-4 text-center font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => handleSort("current_quota")}
                    >
                      Quota<SortIndicator columnKey="current_quota" />
                    </th>
                    <th
                      className="p-4 text-center font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => handleSort("lowest_score")}
                    >
                      {scoreLabel} Low<SortIndicator columnKey="lowest_score" />
                    </th>
                    <th
                      className="p-4 text-center font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => handleSort("best_score")}
                    >
                      {scoreLabel} Best<SortIndicator columnKey="best_score" />
                    </th>
                    <th
                      className="p-4 text-center font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => handleSort("average_score")}
                    >
                      {averageLabel} Avg<SortIndicator columnKey="average_score" />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-navy-800">
                  {sortedQuotas.map((player, index) => (
                    <tr
                      key={index}
                      className={`
                        ${index % 2 === 0
                          ? 'bg-white dark:bg-navy-900'
                          : 'bg-gray-50/80 dark:bg-navy-800/50'
                        }
                        hover:bg-forest-50 dark:hover:bg-navy-700/50
                        transition-colors duration-150
                      `}
                    >
                      <td className="p-4 text-left">
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                          {player.name}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <span className="font-bold text-forest-700 dark:text-forest-400 tabular-nums">
                          {player.current_quota}
                        </span>
                      </td>
                      <td className="p-4 text-center text-gray-600 dark:text-gray-400 tabular-nums">
                        {player.lowest_score}
                      </td>
                      <td className="p-4 text-center">
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">
                          {player.best_score}
                        </span>
                      </td>
                      <td className="p-4 text-center text-gray-600 dark:text-gray-400 tabular-nums">
                        {typeof player.average_score === "number"
                          ? player.average_score.toFixed(1)
                          : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default QuotasPage;
